import React, { useRef, useEffect } from "react";
import "../styles/typography.css";
import styles from "../styles/ChatClassification.module.css";
import MicButton, { MicButtonState } from "./MicButton";
import AudioWaveform from "./AudioWaveform";
import RecordingTimer from "./RecordingTimer";
import { getAssetBaseUrl } from "../config";

interface Props {
  inputText: string;
  setInputText: React.Dispatch<React.SetStateAction<string>>;
  sendMessage: () => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  isLoading: boolean;
  micState: MicButtonState;
  micSupported: boolean;
  onMicClick: () => void;
  onCancelRecording: () => void;
  onCancelTranscription: () => void;
  onCancel: () => void;
  analyserRef: React.RefObject<AnalyserNode | null>;
}

const ChatInput: React.FC<Props> = ({
  inputText,
  setInputText,
  sendMessage,
  handleKeyPress,
  isLoading,
  micState,
  micSupported,
  onMicClick,
  onCancelRecording,
  onCancelTranscription,
  onCancel,
  analyserRef,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isRecording = micState === "recording";
  const isTranscribing = micState === "transcribing";

  // Auto-expande o textarea (estilo ChatGPT)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const maxHeight = 120; // ~5 linhas
      textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + "px";
    }
  }, [inputText]);

  return (
    <div className={styles.chatInput}>
      <div className={`${styles.inputWrapper} ${isRecording ? styles.inputWrapperRecording : ""}`}>
        {isRecording ? (
          <>
            <RecordingTimer />
            <AudioWaveform analyserRef={analyserRef} />
            <button
              type="button"
              onClick={onCancelRecording}
              className={styles.recordingCancelButton}
              aria-label="Cancelar gravação"
              title="Cancelar"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.9a1 1 0 0 0 1.41-1.41L13.41 12l4.9-4.89a1 1 0 0 0 0-1.4z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onMicClick}
              className={styles.recordingConfirmButton}
              aria-label="Confirmar gravação"
              title="Concluir gravação"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </>
        ) : (
          <>
            <textarea
              ref={textareaRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isTranscribing ? "A transcrever áudio..." : "Digite a sua mensagem..."}
              disabled={isTranscribing}
              rows={1}
            />
            <MicButton state={micState} supported={micSupported} onClick={onMicClick} />
            {isTranscribing ? (
              <button
                type="button"
                onClick={onCancelTranscription}
                className={styles.sendInside}
                aria-label="Cancelar transcrição"
                title="Cancelar transcrição"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                  <rect x="5" y="5" width="14" height="14" rx="2" />
                </svg>
              </button>
            ) : isLoading ? (
              <button
                type="button"
                onClick={onCancel}
                className={styles.sendInside}
                aria-label="Cancelar geração"
                title="Cancelar"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                  <rect x="5" y="5" width="14" height="14" rx="2" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => sendMessage()}
                disabled={isTranscribing || !inputText.trim()}
                className={styles.sendInside}
                aria-label="Enviar mensagem"
              >
                <img
                  src={`${getAssetBaseUrl()}images/send-message.png`}
                  alt="Enviar"
                  className={styles.sendIcon}
                />
              </button>
            )}
          </>
        )}
      </div>

      {/* Rodapé (Powered by) */}
      <div className={styles.poweredBy}>
        <img
          src={`${getAssetBaseUrl()}images/only_logo_insia_cinza.png`}
          alt="INSIA"
          className={styles.poweredByLogo}
        />
        <span>Powered by INSIA</span>
      </div>
    </div>
  );
};

export default ChatInput;
